import { create } from "zustand";
import axios from "axios";

const V2_API_URL = `${import.meta.env.VITE_API_V2_URL || "http://localhost:8091/api/v1"}`;

const api = axios.create({
	baseURL: V2_API_URL,
});

api.interceptors.request.use(
	(config) => {
		const token = JSON.parse(localStorage.getItem("auth-storage"))?.state?.token;
		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}
		return config;
	},
	(error) => Promise.reject(error)
);

const DEFAULT_COLUMNS = ["A", "B", "C", "D"];

const toSeatCode = (seat) =>
	String(seat.seat_code || seat.seat_number || "").trim();

const toSeatRow = (seatCode, columns = DEFAULT_COLUMNS) => {
	if (!seatCode) return null;
	if (!/^\d+$/.test(seatCode)) return null;
	const seatNumber = parseInt(seatCode, 10);
	if (Number.isNaN(seatNumber)) return null;
	return Math.floor((seatNumber - 1) / columns.length) + 1;
};

const buildCustomerMap = (reservations) => {
	const map = new Map();
	(reservations || []).forEach((reservation) => {
		const resDay = reservation.RES_DAY || "";
		const resSeq = reservation.RES_SEQ || "";
		if (!resDay || !resSeq) return;
		const ticketId = `${resDay}-${resSeq}`;
		map.set(ticketId, {
			name: reservation.RES_NM || "",
			phoneLast4: reservation.HANDPHONE3 || "",
			totalCount: reservation.TOTAL_CNT || 0,
		});
	});
	return map;
};

export const buildBusSeatStatus = (seats = [], reservations = [], carNumber = "1", seatmapLayout = null) => {
	const customerMap = buildCustomerMap(reservations);

	// 서비스의 car_number로 해당 호차에 모든 좌석 할당
	const key = String(carNumber || "1");
	const rows = new Set();
	let assignedCount = 0;
	const seatData = {};

	seats.forEach((seat) => {
		const seatCode = toSeatCode(seat);
		if (!seatCode) return;

		const row = toSeatRow(seatCode);
		if (row) {
			rows.add(row);
		}

		const assignedUserId = seat.assigned_user_id || "";
		let name = "";
		let lastPhone = "";
		let totalCnt = 0;

		if (assignedUserId) {
			assignedCount += 1;
			const ticketId = assignedUserId.split(".")[0];
			const customer = customerMap.get(ticketId);
			if (customer) {
				name = customer.name;
				lastPhone = customer.phoneLast4;
				totalCnt = customer.totalCount;
			}
		}

		seatData[seatCode] = {
			seat: assignedUserId,
			id: seat.id,
			name,
			last_phone: lastPhone,
			total_cnt: totalCnt,
			is_previous_seat: false,
			available: Boolean(seat.is_held) && !assignedUserId,
		};
	});

	return {
		[key]: {
			cnt: assignedCount,
			rows: Array.from(rows).sort((a, b) => a - b),
			data: seatData,
		},
	};
};

export const useBusStore = create((set) => ({
	isLoading: false,
	error: null,

	clearError: () => set({ error: null }),

	listServices: async (params = {}) => {
		set({ isLoading: true, error: null });
		try {
			const response = await api.get("/bus/info", { params });
			const data = response.data;
			set({ isLoading: false });
			return { success: true, data: data.items || [], total: data.total || 0 };
		} catch (err) {
			const errorMessage =
				err.response?.data?.detail ||
				err.message ||
				"버스 좌석 등록 목록 조회 실패";
			set({ isLoading: false, error: errorMessage });
			return { success: false, error: errorMessage };
		}
	},

	createService: async (payload) => {
		set({ isLoading: true, error: null });
		try {
			const response = await api.post("/bus/info", payload);
			const data = response.data;
			set({ isLoading: false });
			return { success: data?.success !== false, item: data?.item || null };
		} catch (err) {
			const errorMessage =
				err.response?.data?.detail ||
				err.message ||
				"버스 좌석 등록 생성 실패";
			set({ isLoading: false, error: errorMessage });
			return { success: false, error: errorMessage };
		}
	},

	updateService: async (busCode, payload) => {
		set({ isLoading: true, error: null });
		try {
			const response = await api.put(`/bus/info/${busCode}`, payload);
			const data = response.data;
			set({ isLoading: false });
			return { success: data?.success !== false, item: data?.item || null };
		} catch (err) {
			const errorMessage =
				err.response?.data?.detail ||
				err.message ||
				"버스 좌석 등록 수정 실패";
			set({ isLoading: false, error: errorMessage });
			return { success: false, error: errorMessage };
		}
	},

	deleteService: async (busCode) => {
		set({ isLoading: true, error: null });
		try {
			const response = await api.delete(`/bus/info/${busCode}`);
			const data = response.data;
			set({ isLoading: false });
			return { success: data?.success !== false };
		} catch (err) {
			const errorMessage =
				err.response?.data?.detail ||
				err.message ||
				"버스 좌석 등록 삭제 실패";
			set({ isLoading: false, error: errorMessage });
			return { success: false, error: errorMessage };
		}
	},

	getSeats: async (busCode) => {
		set({ isLoading: true, error: null });
		try {
			const response = await api.get("/bus/seats", {
				params: { bus_code: busCode },
			});
			const data = response.data;
			set({ isLoading: false });
			return { success: true, data };
		} catch (err) {
			const errorMessage =
				err.response?.data?.detail || err.message || "버스 좌석 조회 실패";
			set({ isLoading: false, error: errorMessage });
			return { success: false, error: errorMessage };
		}
	},

	getSeatmap: async (busCode, layoutId = null, variant = null) => {
		set({ isLoading: true, error: null });
		try {
			const response = await api.get("/bus/seatmap", {
				params: {
					bus_code: busCode,
					layout_id: layoutId,
					variant,
				},
			});
			const data = response.data;
			set({ isLoading: false });
			return {
				success: data?.success !== false,
				seatmapLayout: data?.seatmap_layout || null,
			};
		} catch (err) {
			const errorMessage =
				err.response?.data?.detail || err.message || "버스 좌석배치도 조회 실패";
			set({ isLoading: false, error: errorMessage });
			return { success: false, error: errorMessage };
		}
	},

	runAssignment: async (payload) => {
		set({ isLoading: true, error: null });
		try {
			const response = await api.post("/bus/run-assignment", payload);
			const data = response.data;
			set({ isLoading: false });
			return {
				success: data?.success !== false,
				message: data?.message || "",
				data: {
					seatStatus: data?.seat_status || {},
					seatmapLayout: data?.seatmap_layout || null,
					results: data?.results || [],
				},
			};
		} catch (err) {
			const errorMessage =
				err.response?.data?.detail || err.message || "버스 자동 배정 실패";
			set({ isLoading: false, error: errorMessage });
			return { success: false, error: errorMessage };
		}
	},

	confirmAssignment: async (payload) => {
		set({ isLoading: true, error: null });
		try {
			const response = await api.post("/bus/confirm-assignment", payload);
			const data = response.data;
			set({ isLoading: false });
			return { success: data?.success !== false, data };
		} catch (err) {
			const errorMessage =
				err.response?.data?.detail || err.message || "버스 배정 확정 실패";
			set({ isLoading: false, error: errorMessage });
			return { success: false, error: errorMessage };
		}
	},

	cancelAssignments: async (busCode) => {
		set({ isLoading: true, error: null });
		try {
			const response = await api.post("/bus/assign/cancel-all", null, {
				params: { bus_code: busCode },
			});
			const data = response.data;
			set({ isLoading: false });
			return { success: data?.success !== false, data };
		} catch (err) {
			const errorMessage =
				err.response?.data?.detail || err.message || "버스 배정 취소 실패";
			set({ isLoading: false, error: errorMessage });
			return { success: false, error: errorMessage };
		}
	},

	assignSeat: async (busSeatId, userId) => {
		set({ isLoading: true, error: null });
		try {
			const response = await api.post("/bus/assign", {
				bus_seat_id: busSeatId,
				user_id: userId,
			});
			const data = response.data;
			set({ isLoading: false });
			return { success: data?.success !== false, data };
		} catch (err) {
			const errorMessage =
				err.response?.data?.detail || err.message || "버스 좌석 배정 실패";
			set({ isLoading: false, error: errorMessage });
			return { success: false, error: errorMessage };
		}
	},

	cancelSeat: async (busSeatId) => {
		set({ isLoading: true, error: null });
		try {
			const response = await api.post("/bus/assign/cancel", {
				bus_seat_id: busSeatId,
			});
			const data = response.data;
			set({ isLoading: false });
			return { success: data?.success !== false, data };
		} catch (err) {
			const errorMessage =
				err.response?.data?.detail || err.message || "버스 배정 취소 실패";
			set({ isLoading: false, error: errorMessage });
			return { success: false, error: errorMessage };
		}
	},
}));
